import { Fragment, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Minus, Info } from 'lucide-react';

const COLUMNS = [
  { id: 'essential',    label: 'Essential',    price: '£49',  highlight: false },
  { id: 'professional', label: 'Professional', price: '£99',  highlight: true  },
  { id: 'business',     label: 'Business',     price: '£199', highlight: false },
];

const TICK = 'tick';
const DASH = 'dash';

const GROUPS = [
  {
    title: 'Core Monitoring',
    rows: [
      { feature: 'Weekly automated scan',          essential: TICK, professional: TICK, business: TICK },
      { feature: 'SSL certificate grade',          essential: TICK, professional: TICK, business: TICK },
      { feature: 'Security headers check',         essential: TICK, professional: TICK, business: TICK },
      { feature: 'DNS security — SPF and DMARC',   essential: TICK, professional: TICK, business: TICK },
      { feature: 'Breach exposure monitoring',     essential: TICK, professional: TICK, business: TICK },
      { feature: 'Domain blacklist check',         essential: TICK, professional: TICK, business: TICK },
      { feature: 'Weekly plain English report',    essential: TICK, professional: TICK, business: TICK },
      { feature: 'Client dashboard',               essential: TICK, professional: TICK, business: TICK },
    ],
  },
  {
    title: 'Real-Time Protection',
    rows: [
      { feature: 'Real-time critical alerts',      essential: DASH, professional: TICK, business: TICK },
      { feature: 'CVE vulnerability indicators',   essential: DASH, professional: TICK, business: TICK },
      { feature: 'Homepage change detection',      essential: DASH, professional: TICK, business: TICK },
      { feature: 'Certificate transparency',       essential: DASH, professional: TICK, business: TICK },
      { feature: 'Uptime monitoring — 5 min',      essential: DASH, professional: TICK, business: TICK },
      { feature: '12 months scan history',         essential: DASH, professional: TICK, business: TICK },
    ],
  },
  {
    title: 'Compliance and Advisory',
    rows: [
      {
        feature: 'Cyber Essentials technical readiness check',
        essential: DASH, professional: DASH, business: TICK,
        tooltip: 'Automated technical assessment only. Official Cyber Essentials certification requires an IASME-accredited assessor. Acodia can refer you to an accredited assessor when you are ready.',
      },
      {
        feature: 'GDPR technical surface check',
        essential: DASH, professional: DASH, business: TICK,
        tooltip: 'Technical check of publicly visible GDPR indicators including cookie consent, privacy policy presence, SSL on forms, and data transfer disclosures. This is not a legal GDPR compliance audit. For full GDPR compliance consult a qualified data protection professional.',
      },
      { feature: 'Monthly 30 min strategy call',   essential: DASH, professional: DASH, business: TICK },
      { feature: 'Dedicated account contact',      essential: DASH, professional: DASH, business: TICK },
      { feature: 'Custom alert thresholds',        essential: DASH, professional: DASH, business: TICK },
      { feature: 'Scan results API access',        essential: DASH, professional: DASH, business: TICK },
    ],
  },
  {
    title: 'Support',
    rows: [
      { feature: 'Support channel',                essential: 'Email',    professional: 'Priority email', business: '4hr response' },
      { feature: 'Extra domain pricing',           essential: '+£19/mo',  professional: '+£29/mo',        business: '+£39/mo' },
      { feature: 'Free trial',                     essential: '7 days',   professional: '7 days',         business: '7 days' },
    ],
  },
];

const PRO_TINT = 'rgba(139,92,246,0.06)';

function InfoTooltip({ text, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex items-center align-middle"
      style={{ marginLeft: 6 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="More information"
        data-testid={testId}
        tabIndex={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: 'var(--ts)',
          cursor: 'help',
          lineHeight: 0,
        }}
      >
        <Info size={13} />
      </button>
      {open && (
        <span
          role="tooltip"
          className="font-body"
          style={{
            position: 'absolute',
            zIndex: 30,
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 280,
            maxWidth: '90vw',
            padding: '0.6rem 0.75rem',
            background: 'var(--bg)',
            border: '1px solid var(--b2)',
            borderRadius: 6,
            color: 'var(--tm)',
            fontSize: '0.75rem',
            lineHeight: 1.55,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
            whiteSpace: 'normal',
            textAlign: 'left',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function FeatureLabel({ row, idPrefix }) {
  return (
    <>
      {row.feature}
      {row.tooltip && (
        <InfoTooltip
          text={row.tooltip}
          testId={`plan-comparison-tooltip-${idPrefix}-${row.feature.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
        />
      )}
    </>
  );
}

function CellValue({ value }) {
  if (value === TICK) {
    return (
      <span
        className="font-body inline-block"
        style={{ color: 'var(--ok)', fontWeight: 600, fontSize: '0.95rem' }}
        aria-label="included"
      >
        ✓
      </span>
    );
  }
  if (value === DASH) {
    return (
      <span
        className="font-body inline-block"
        style={{ color: 'var(--ts)', fontSize: '0.95rem' }}
        aria-label="not included"
      >
        —
      </span>
    );
  }
  return (
    <span
      className="font-body"
      style={{ fontSize: '0.8rem', color: 'var(--tm)' }}
    >
      {value}
    </span>
  );
}

function DesktopTable() {
  return (
    <div
      data-testid="plan-comparison-desktop"
      className="hidden md:block"
      style={{
        background: 'var(--l1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--b1)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <table className="w-full" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '40%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
            <th
              className="font-body"
              style={{
                textAlign: 'left',
                padding: '0.85rem 1rem',
                color: 'var(--white)',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Feature
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col.id}
                className="font-body"
                style={{
                  textAlign: 'center',
                  padding: '0.85rem 1rem',
                  color: 'var(--white)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: col.highlight ? PRO_TINT : 'transparent',
                  verticalAlign: 'top',
                }}
              >
                <div>{`${col.label} ${col.price}`}</div>
                {col.highlight && (
                  <div
                    className="font-mono mt-1"
                    style={{
                      fontSize: '0.6rem',
                      color: 'var(--a)',
                      letterSpacing: '0.08em',
                      fontWeight: 500,
                    }}
                  >
                    ★ Most Popular
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {GROUPS.map((group, gi) => (
            <Fragment key={`g-${gi}`}>
              <tr>
                <td
                  colSpan={4}
                  className="font-body uppercase"
                  style={{
                    fontSize: '0.7rem',
                    color: 'var(--ts)',
                    letterSpacing: '0.08em',
                    padding: '0.75rem 1rem 0.25rem',
                    borderTop: '1px solid var(--b1)',
                  }}
                >
                  {group.title}
                </td>
              </tr>
              {group.rows.map((row, ri) => {
                const odd = ri % 2 === 0;
                const baseBg = odd ? 'rgba(255,255,255,0.02)' : 'transparent';
                return (
                  <tr key={`g-${gi}-r-${ri}`} style={{ background: baseBg, minHeight: 44 }}>
                    <td
                      className="font-body"
                      style={{
                        padding: '0.6rem 1rem',
                        color: 'var(--tm)',
                        fontSize: '0.82rem',
                        position: 'relative',
                      }}
                    >
                      <FeatureLabel row={row} idPrefix="desktop" />
                    </td>
                    <td style={{ padding: '0.6rem 1rem', textAlign: 'center' }}>
                      <CellValue value={row.essential} />
                    </td>
                    <td
                      style={{
                        padding: '0.6rem 1rem',
                        textAlign: 'center',
                        background: PRO_TINT,
                      }}
                    >
                      <CellValue value={row.professional} />
                    </td>
                    <td style={{ padding: '0.6rem 1rem', textAlign: 'center' }}>
                      <CellValue value={row.business} />
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileSinglePlan() {
  const [index, setIndex] = useState(1);
  const col = COLUMNS[index];
  const colKey = col.id;

  return (
    <div
      data-testid="plan-comparison-mobile"
      className="md:hidden"
      style={{
        background: 'var(--l1)',
        border: '1px solid var(--b1)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{
          padding: '0.85rem 1rem',
          background: col.highlight ? PRO_TINT : 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid var(--b1)',
        }}
      >
        <button
          type="button"
          aria-label="Previous plan"
          data-testid="plan-comparison-mobile-prev"
          onClick={() => setIndex((i) => (i - 1 + COLUMNS.length) % COLUMNS.length)}
          style={{
            color: 'var(--ts)',
            background: 'transparent',
            padding: 4,
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <div
            className="font-body"
            style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--white)' }}
          >
            {col.label} {col.price}
          </div>
          {col.highlight && (
            <div
              className="font-mono mt-1"
              style={{ fontSize: '0.6rem', color: 'var(--a)', letterSpacing: '0.08em' }}
            >
              ★ Most Popular
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Next plan"
          data-testid="plan-comparison-mobile-next"
          onClick={() => setIndex((i) => (i + 1) % COLUMNS.length)}
          style={{
            color: 'var(--ts)',
            background: 'transparent',
            padding: 4,
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div>
        {GROUPS.map((group, gi) => (
          <div key={`mg-${gi}`}>
            <div
              className="font-body uppercase"
              style={{
                fontSize: '0.7rem',
                color: 'var(--ts)',
                letterSpacing: '0.08em',
                padding: '0.75rem 1rem 0.25rem',
                borderTop: '1px solid var(--b1)',
              }}
            >
              {group.title}
            </div>
            {group.rows.map((row, ri) => {
              const odd = ri % 2 === 0;
              return (
                <div
                  key={`mg-${gi}-r-${ri}`}
                  className="flex items-center justify-between"
                  style={{
                    padding: '0.6rem 1rem',
                    background: odd ? 'rgba(255,255,255,0.02)' : 'transparent',
                  }}
                >
                  <span
                    className="font-body"
                    style={{ fontSize: '0.82rem', color: 'var(--tm)', position: 'relative' }}
                  >
                    <FeatureLabel row={row} idPrefix="mobile" />
                  </span>
                  <span style={{ marginLeft: '1rem' }}>
                    <CellValue value={row[colKey]} />
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

const FAQ_ITEMS = [
  {
    q: 'Can I change my plan after signing up?',
    a: 'Yes — you can upgrade or downgrade at any time from your dashboard. Changes take effect at your next billing date.',
  },
  {
    q: 'What happens after the 7-day free trial?',
    a: 'Your card is charged automatically on day 8 unless you cancel before then. You can cancel any time from your dashboard with one click.',
  },
  {
    q: 'Do I need any technical knowledge to use Acodia Watch?',
    a: 'None at all. Every report is written in plain English with specific actions. If anything needs a developer we tell you exactly what to ask them.',
  },
];

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState(-1);
  return (
    <div
      data-testid="plan-comparison-faq"
      className="mx-auto"
      style={{ maxWidth: 700, marginTop: '2rem' }}
    >
      {FAQ_ITEMS.map((item, i) => {
        const open = openIndex === i;
        return (
          <div
            key={item.q}
            style={{ borderBottom: '1px solid var(--b1)' }}
          >
            <button
              type="button"
              data-testid={`plan-comparison-faq-q-${i}`}
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? -1 : i)}
              className="font-body w-full flex items-center justify-between text-left"
              style={{
                padding: '0.9rem 0',
                background: 'transparent',
                color: 'var(--white)',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              <span>{item.q}</span>
              {open ? (
                <Minus size={16} style={{ color: 'var(--ts)', flexShrink: 0, marginLeft: 12 }} />
              ) : (
                <Plus size={16} style={{ color: 'var(--ts)', flexShrink: 0, marginLeft: 12 }} />
              )}
            </button>
            <div
              style={{
                maxHeight: open ? 240 : 0,
                overflow: 'hidden',
                transition: 'max-height 300ms ease',
              }}
            >
              <p
                className="font-body"
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--tm)',
                  lineHeight: 1.65,
                  padding: '0.75rem 0',
                }}
              >
                {item.a}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PlanComparisonTable() {
  const scrollToMobile = (e) => {
    e.preventDefault();
    const el = document.getElementById('plan-comparison-mobile-anchor');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      data-testid="plan-comparison-section"
      className="py-12 sm:py-16"
      style={{ background: 'var(--bg)' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2
            className="font-display"
            style={{
              fontWeight: 700,
              fontSize: 'clamp(1.4rem, 2.5vw, 1.8rem)',
              color: 'var(--white)',
              letterSpacing: '-0.01em',
            }}
          >
            Compare All Features
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '0.875rem',
              color: 'var(--ts)',
              marginTop: '0.5rem',
              marginBottom: '2rem',
            }}
          >
            Everything included in each plan
          </p>
        </div>

        <div className="md:hidden text-center mb-4">
          <a
            href="#plan-comparison-mobile-anchor"
            onClick={scrollToMobile}
            data-testid="plan-comparison-mobile-link"
            className="font-body underline-offset-2 hover:underline"
            style={{ fontSize: '0.85rem', color: 'var(--a)' }}
          >
            View full comparison ↓
          </a>
        </div>

        <div className="mx-auto" style={{ maxWidth: 900 }}>
          <DesktopTable />
          <div id="plan-comparison-mobile-anchor" />
          <MobileSinglePlan />
        </div>

        <FaqAccordion />
      </div>
    </section>
  );
}
